import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import Loader from '../../components/Loader';
import Paginator from '../../components/Paginator';
import { toast } from 'react-toastify';
import { formatCurrency, formatDate, formatDateLocal } from '../../utils/helper';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const List = () => {
    const [expenses, setExpenses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [categories, setCategories] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    
    const [searchParams] = useSearchParams();
    
    const [apiData, setApiData] = useState({
        search: "",
        page: 1,
        start_date: "",
        end_date: "",
        expense_category_id: ""
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        getExpenses();
    }, [apiData.page, apiData.search, apiData.start_date, apiData.end_date, apiData.expense_category_id]);

    const fetchCategories = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/expense-categories?per_page=100'
            });
            if (response.data.status) {
                setCategories(response.data.data.categories);
            }
        } catch (error) {
            console.error('Failed to fetch categories', error);
        }
    };

    const handleDateChange = (update) => {
        setDateRange(update);
        const [start, end] = update;
        setApiData(prev => ({
            ...prev,
            start_date: start ? formatDateLocal(start) : "",
            end_date: end ? formatDateLocal(end) : ""
        }));
    };

    const getExpenses = async () => {
        setIsLoading(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/expenses',
                data: apiData
            });
            if (response.data.status) {
                const result = response.data.data.expenses;
                setExpenses(result.data || []);
                setTotalAmount(response.data.data.total_amount || 0);
                setPagination({
                    total: result.total,
                    per_page: result.per_page,
                    current_page: result.current_page,
                    last_page: result.last_page,
                });
            }
        } catch (error) {
            toast.error('Failed to fetch expenses');
        } finally {
            setIsLoading(false);
        }
    };

    const deleteItem = async (id) => {
        if (!window.confirm('Are you sure you want to delete this expense?')) return;
        
        try {
            const response = await ApiService.request({
                method: 'DELETE',
                url: `admin/expenses/${id}`
            });
            if (response.data.status) {
                toast.success(response.data.message);
                getExpenses();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to delete expense');
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Expenses</h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <OverlayTrigger placement="top" overlay={<Tooltip>Create Expense</Tooltip>}>
                        <Link to="/expenses/create" className="btn btn-sm fw-bold btn-base">Create Expense</Link>
                    </OverlayTrigger>
                    </div>
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="d-flex align-items-center position-relative my-1">
                                    <span className="svg-icon svg-icon-1 position-absolute ms-6">
                                        <i className="fas fa-search fs-1"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control form-control-solid w-250px ps-14"
                                        placeholder="Search Expense"
                                        value={apiData.search}
                                        onChange={(e) => setApiData({ ...apiData, search: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex justify-content-end gap-3">
                                    <div className="w-200px">
                                        <select
                                            className="form-select form-select-solid"
                                            value={apiData.expense_category_id}
                                            onChange={(e) => setApiData({ ...apiData, expense_category_id: e.target.value })}
                                        >
                                            <option value="">All Categories</option>
                                            {categories.map((cat) => (
                                                <option key={cat.id} value={cat.id}>
                                                    {cat.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="w-200px">
                                         <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={handleDateChange}
                                            className="form-control form-control-solid"
                                            placeholderText="Select Date Range"
                                            isClearable={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card-body py-4">
                            <div className="d-flex align-items-center bg-light-primary rounded p-3 mb-5">
                                <span className="text-gray-600 fw-bold me-2 fs-6">Total Expense:</span>
                                <span className="text-primary fw-bold fs-3">{formatCurrency(totalAmount)}</span>
                            </div>
                            {isLoading ? <Loader /> : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table align-middle table-row-dashed fs-6 gy-5">
                                            <thead>
                                                <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                    <th>Date</th>
                                                    <th>Reference</th>
                                                    <th>Category</th>
                                                    <th>Amount</th>
                                                    <th>Note</th>
                                                    <th className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-gray-600 fw-semibold">
                                                {expenses.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{formatDate(item.expense_date)}</td>
                                                        <td>{item.reference_no}</td>
                                                        <td>{item.category?.name || 'N/A'}</td>
                                                        <td>{formatCurrency(item.amount)}</td>
                                                        <td>{item.description}</td>
                                                        <td className="text-end">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Edit Expense</Tooltip>}>
                                                                <Link to={`/expenses/edit/${item.id}`} className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                                                                    <i className="fas fa-edit"></i>
                                                                </Link>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Delete Expense</Tooltip>}>
                                                                <button onClick={() => deleteItem(item.id)} className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                                                                    <i className="fas fa-trash"></i>
                                                                </button>
                                                            </OverlayTrigger>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {expenses.length === 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-center">No expenses found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Paginator pagination={pagination} onPageChange={(page) => setApiData({ ...apiData, page })} />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
